import {
  RoiCalculationInput,
  RoiCalculationResult,
} from "../types/roiCalculations";

// Constants
const DAYS_PER_YEAR = 264; // 22 days per month * 12 months
const FOWND_CONSTANTS = {
  DOC_TIME_WITH_FOWND: 5, // minutes
  ADDITIONAL_PATIENTS_PER_DAY: 2,
  ACCURACY_IMPROVEMENT: 5, // percentage
  COMPLIANCE_VALUE: 5000, // dollars
  MONTHLY_SUBSCRIPTION: 1800,
  COST_PER_SESSION: 10,
  REIMBURSEMENT_PER_SESSION: 100,
};

export const calculateRoi = (
  input: RoiCalculationInput
): RoiCalculationResult => {
  const { clinicSize, timeMetrics, revenueMetrics } = input;
  const numTherapists = clinicSize.small.clinicians;
  const sessionsPerDay = revenueMetrics.sessionsPerDay.low;
  const ptSalary = revenueMetrics.ptSalaryPerHour.low;
  const docTimeWithoutFownd =
    timeMetrics.documentationTimePerSession.withoutFownd.low;

  // 1. Calculate Time Savings (ensure positive value)
  const minutesSavedPerSession = Math.abs(
    docTimeWithoutFownd - FOWND_CONSTANTS.DOC_TIME_WITH_FOWND
  );
  const hoursSavedPerDay = (minutesSavedPerSession * sessionsPerDay) / 60;
  const annualHoursSaved = hoursSavedPerDay * DAYS_PER_YEAR * numTherapists;
  const timeSavingsValue = annualHoursSaved * ptSalary;

  // 2. Calculate Additional Revenue
  const annualSessions = sessionsPerDay * DAYS_PER_YEAR * numTherapists;

  // Revenue from additional patients
  const additionalPatientsRevenue =
    FOWND_CONSTANTS.ADDITIONAL_PATIENTS_PER_DAY *
    FOWND_CONSTANTS.REIMBURSEMENT_PER_SESSION *
    DAYS_PER_YEAR *
    numTherapists;

  // Revenue from improved accuracy
  const accuracyRevenue =
    (annualSessions *
      FOWND_CONSTANTS.REIMBURSEMENT_PER_SESSION *
      FOWND_CONSTANTS.ACCURACY_IMPROVEMENT) /
    100;

  // 3. Calculate Total Benefits
  const totalBenefits =
    timeSavingsValue +
    additionalPatientsRevenue +
    accuracyRevenue +
    FOWND_CONSTANTS.COMPLIANCE_VALUE;

  // 4. Calculate Total Costs
  const annualSubscriptionCost = FOWND_CONSTANTS.MONTHLY_SUBSCRIPTION * 12;
  const sessionCosts = annualSessions * FOWND_CONSTANTS.COST_PER_SESSION;
  const totalCosts = annualSubscriptionCost + sessionCosts;

  // 5. Calculate Net Profit and ROI
  const netProfit = totalBenefits - totalCosts;
  const roiPercentage = (netProfit / totalCosts) * 100;
  const roiMultiplier = 1 + netProfit / totalCosts;

  const revenueGain = timeSavingsValue + additionalPatientsRevenue + accuracyRevenue;

  return {
    timeSavings: {
      hoursPerDay: {
        low: hoursSavedPerDay,
        high: hoursSavedPerDay * 1.2, // 20% higher estimate
      },
      annualHoursSaved: {
        low: annualHoursSaved,
        high: annualHoursSaved * 1.2,
      },
      monetaryValue: {
        low: timeSavingsValue,
        high: timeSavingsValue * 1.2,
      },
    },
    additionalRevenue: {
      fromTimeSavings: {
        low: timeSavingsValue,
        high: timeSavingsValue * 1.2,
      },
      fromThroughput: {
        low: additionalPatientsRevenue,
        high: additionalPatientsRevenue * 1.2,
      },
      fromAccuracy: {
        low: accuracyRevenue,
        high: accuracyRevenue * 1.2,
      },
    },
    totalIncentive: {
      low: totalBenefits,
      high: totalBenefits * 1.2,
    },
    totalCost: {
      low: totalCosts,
      high: totalCosts * 1.1, // 10% higher estimate
    },
    netProfit: {
      low: netProfit,
      high: netProfit * 1.2,
    },
    roi: {
      percentage: {
        low: roiPercentage,
        high: roiPercentage * 1.2,
      },
      multiplier: {
        low: roiMultiplier,
        high: roiMultiplier * 1.2,
      },
    },
    perTherapist: {
      revenueGain: {
        low: revenueGain / numTherapists,
        high: (revenueGain / numTherapists) * 1.2,
      },
      hoursSaved: {
        low: annualHoursSaved / numTherapists,
        high: (annualHoursSaved / numTherapists) * 1.2,
      },
      costSavings: {
        low: netProfit / numTherapists,
        high: (netProfit / numTherapists) * 1.2,
      },
    },
  };
};

// Default values that will be overridden by form inputs
export const defaultRoiInput: RoiCalculationInput = {
  clinicSize: {
    small: {
      clinicians: 1,
      annualSessions: { low: 0, high: 0 },
    },
    medium: {
      clinicians: { low: 1, high: 1 },
      annualSessions: { low: 0, high: 0 },
    },
  },
  timeMetrics: {
    documentationTimePerSession: {
      withoutFownd: { low: 0, high: 0 },
      withFownd: {
        low: FOWND_CONSTANTS.DOC_TIME_WITH_FOWND,
        high: FOWND_CONSTANTS.DOC_TIME_WITH_FOWND,
      },
    },
    documentationTimePerDay: {
      withoutFownd: { low: 0, high: 0 },
      withFownd: { low: 0, high: 0 },
    },
  },
  revenueMetrics: {
    sessionRevenue: FOWND_CONSTANTS.REIMBURSEMENT_PER_SESSION,
    daysPerMonth: 22,
    sessionsPerDay: { low: 0, high: 0 },
    averageReimbursement: FOWND_CONSTANTS.REIMBURSEMENT_PER_SESSION,
    ptSalaryPerHour: { low: 0, high: 0 },
  },
  fowndMetrics: {
    increasedThroughput: {
      low: FOWND_CONSTANTS.ADDITIONAL_PATIENTS_PER_DAY,
      high: FOWND_CONSTANTS.ADDITIONAL_PATIENTS_PER_DAY,
    },
    improvedAccuracy: {
      low: FOWND_CONSTANTS.ACCURACY_IMPROVEMENT,
      high: FOWND_CONSTANTS.ACCURACY_IMPROVEMENT,
    },
    complianceRiskValue: FOWND_CONSTANTS.COMPLIANCE_VALUE,
    subscriptionCost: FOWND_CONSTANTS.MONTHLY_SUBSCRIPTION,
    sessionCost: FOWND_CONSTANTS.COST_PER_SESSION,
  },
};
